import axios from "axios";

// ── API base — auto-switches, no more forgetting to comment/uncomment ──
// 1. If REACT_APP_API_URL is set in your .env / build config, that wins.
// 2. Otherwise: localhost when you're actually developing on localhost,
//    production URL for every real device (phones, other laptops, etc).
// This is what was hanging on mobile — the old hardcoded localhost:8080
// meant every phone tried to call itself and just timed out silently.
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:8080"
    : "https://api.sivalayamthokada.org");

// Global timeout — prevents the app from spinning forever on a bad
// connection. Any call that takes longer than this fails fast so the UI
// can show a retry state instead of an endless spinner.
axios.defaults.timeout = 15000;

// 📦 Public APIs
export const fetchPhotos = () => axios.get(`${API_BASE}/api/photos`);
export const fetchDonations = () => axios.get(`${API_BASE}/api/donations`);
export const fetchTopDonations = () =>
  axios.get(`${API_BASE}/api/topdonations?num=20`);
export const fetchTotal = () => axios.get(`${API_BASE}/api/donations/total`);
export const fetchExpenditures = () =>
  axios.get(`${API_BASE}/api/expenditures`);
export const fetchTotalSpent = () =>
  axios.get(`${API_BASE}/api/expenditures/total`);

// 🔒 Reveal-gated summary — use THIS on the Expenditures page instead of
// fetchTotal / fetchTotalSpent above.
export const fetchExpenditureSummary = () =>
  axios.get(`${API_BASE}/api/expenditures/summary`);

// 🗳️ Votes - Public (cast only, no count returned)
export const castVote = () => axios.post(`${API_BASE}/api/votes`);

// 🆕 Banners - Public
export const fetchActiveBanners = () =>
  axios.get(`${API_BASE}/api/banners/active`);

// 🔐 Auth APIs
export const login = (mobile, password) =>
  axios.post(`${API_BASE}/auth/login`, { mobile, password });

export const requestOtp = (mobile) =>
  axios.post(`${API_BASE}/auth/request-otp`, { mobile });

export const verifyOtp = (mobile, otp) =>
  axios.post(`${API_BASE}/auth/verify-otp`, { mobile, otp });

// 🔒 Admin APIs (require Bearer Token)
export const uploadDonation = (donation, token) =>
  axios.post(`${API_BASE}/api/admin/donations`, donation, {
    headers: { Authorization: `Bearer ${token}` },
  });

// 🆕 Above ₹50,000 Donors
export const fetchAboveFifty = () => axios.get(`${API_BASE}/api/aboveFifty`);

export const uploadPhoto = (file, token) => {
  const formData = new FormData();
  formData.append("file", file);
  return axios.post(`${API_BASE}/api/admin/photos`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
  });
};

export const searchDonorsByFirstName = (firstName, token) =>
  axios.get(`${API_BASE}/api/admin/donations/search?firstName=${firstName}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const updateDonation = (id, updatedDonation, token) =>
  axios.put(`${API_BASE}/api/admin/donations/${id}`, updatedDonation, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const deleteDonation = (id, token) =>
  axios.delete(`${API_BASE}/api/admin/donations/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

// 🆕 Pledges APIs
export const createPledge = (pledge) =>
  axios.post(`${API_BASE}/api/pledge/add`, pledge);

export const fetchAllPledges = (token) =>
  axios.get(`${API_BASE}/api/admin/pledges`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const deletePledge = (id, token) =>
  axios.delete(`${API_BASE}/api/admin/pledges/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

// 📦 Expenditures - Admin Only
export const uploadExpenditure = (exp, token) =>
  axios.post(`${API_BASE}/api/admin/expenditures`, exp, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const updateExpenditure = (id, exp, token) =>
  axios.put(`${API_BASE}/api/admin/expenditures/${id}`, exp, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const deleteExpenditure = (id, token) =>
  axios.delete(`${API_BASE}/api/admin/expenditures/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const fetchAllExpenditures = (token) =>
  axios.get(`${API_BASE}/api/admin/expenditures`, {
    headers: { Authorization: `Bearer ${token}` },
  });

// 🆕 Banners - Admin Only
export const fetchAllBanners = (token) =>
  axios.get(`${API_BASE}/api/admin/banners`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const createBanner = (banner, token) =>
  axios.post(`${API_BASE}/api/admin/banners`, banner, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const updateBanner = (id, banner, token) =>
  axios.put(`${API_BASE}/api/admin/banners/${id}`, banner, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const deleteBanner = (id, token) =>
  axios.delete(`${API_BASE}/api/admin/banners/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

// 🗳️ Votes - Admin Only
export const fetchVoteCount = (token) =>
  axios.get(`${API_BASE}/api/admin/votes/count`, {
    headers: { Authorization: `Bearer ${token}` },
  });

// 🔓 Reveal control - Admin Only
export const fetchRevealStatus = (token) =>
  axios.get(`${API_BASE}/api/admin/reveal-status`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const setRevealStatus = (revealed, token) =>
  axios.put(
    `${API_BASE}/api/admin/reveal-status`,
    { revealed },
    { headers: { Authorization: `Bearer ${token}` } },
  );
